import React from 'react';
import { HashRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import HomePage from './pages/HomePage';
import CoursesPage from './pages/CoursesPage';
import ResourcesPage from './pages/ResourcesPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import ContactPage from './pages/ContactPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import ManageCoursesPage from './pages/admin/ManageCoursesPage';
import ManageResourcesPage from './pages/admin/ManageResourcesPage';
import ManageTeamPage from './pages/admin/ManageTeamPage';
import ManageSiteContentPage from './pages/admin/ManageSiteContentPage';
import ManageStudentTestimoniesPage from './pages/admin/ManageStudentTestimoniesPage';
import ManageContactsPage from './pages/admin/ManageContactsPage';
import AdminLayout from './pages/admin/AdminLayout';
import { useAuth } from './contexts/AuthContext';
import Header from './components/Header';
import CourseDetailPage from './pages/CourseDetailPage';
import ManageCourseDetailsPage from './pages/admin/ManageCourseDetailsPage';
import CourseV2Page from './pages/CourseV2Page';
import CourseV2DetailPage from './pages/CourseV2DetailPage';
import ManageCourseV2Page from './pages/admin/ManageCourseV2Page';
import { ThemeProvider } from './contexts/ThemeContext';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import PageProgress from './components/PageProgress';

// Protected Route component - only used for authenticated routes
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

// AdminRoute: Only allows authenticated admin users
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();
  
  // If not authenticated at all, redirect to login
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  // If authenticated but not admin, also redirect to login
  // This ensures users need to login with admin credentials
  if (user.role.toLowerCase() !== 'admin') {
    return <Navigate to="/login" state={{ from: location, requireAdmin: true }} replace />;
  }
  
  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <HashRouter>
        <Header />
        <PageProgress />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/courses" element={<CoursesPage />} />
          <Route path="/courses/:courseId" element={<CourseDetailPage />} />
          <Route path="/coursesv2" element={<CourseV2Page />} />
          <Route path="/coursesv2/:courseId" element={<CourseV2DetailPage />} />
          <Route path="/resources" element={<ResourcesPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/contact" element={<ContactPage />} />

          {/* Admin Routes - Protected by AdminRoute */}
          <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<AdminDashboardPage />} />
            <Route path="courses" element={<ManageCoursesPage />} />
            <Route path="courses/:courseId/details" element={<ManageCourseDetailsPage />} />
            <Route path="coursesv2" element={<ManageCourseV2Page />} />
            <Route path="resources" element={<ManageResourcesPage />} />
            <Route path="team" element={<ManageTeamPage />} />
            <Route path="content" element={<ManageSiteContentPage />} />
            <Route path="testimonies" element={<ManageStudentTestimoniesPage />} />
            <Route path="contacts" element={<ManageContactsPage />} />
            <Route path="settings" element={<AdminSettingsPage />} />
          </Route>

          {/* Protected Routes - Only Dashboard requires authentication */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />


          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </HashRouter>
    </ThemeProvider>
  );
};

export default App;
